import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';
import { ValuePlaceholder } from '@openx/components-core/lib/Value/ValuePlaceholder/ValuePlaceholder';
import { bigNumberWithCommas } from '@openx/utils/lib/bigNumberWithCommas';
import { type JSX, type ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { getSectionLabels } from '../../../constants';
import type { SectionLabels, Sections } from '../../../types';

function renderReachValue(value?: bigint): string | JSX.Element {
  if (value) {
    return bigNumberWithCommas(value);
  }

  return <ValuePlaceholder />;
}

const renderSections = (sections: Sections, sectionLabels: SectionLabels): ReactNode => {
  return Object.entries(sections).map(([sectionType, value]) => {
    const sectionLabel = sectionLabels[sectionType];

    if (!sectionLabel) {
      return null;
    }

    return (
      <Typography
        component="span"
        key={`${sectionType}.${value}`}
        variant="body2"
        data-test={`reach-${sectionType}`}
        sx={{
          display: 'block',
          lineHeight: 'inherit',
          mt: 0.75,
        }}
      >
        <Typography
          component="span"
          variant="body2Bold"
          sx={{
            lineHeight: 'inherit',
          }}
        >
          {sectionLabel}
        </Typography>
        : {renderReachValue(value)}
      </Typography>
    );
  });
};

type Props = {
  loading: boolean;
  sections: Sections;
  text: string;
};

export function ReachTooltipContent({ loading, sections, text }: Props): JSX.Element {
  const { t } = useTranslation();

  const sectionLabels = useMemo(() => getSectionLabels(t), [t]);

  if (loading) {
    return (
      <Box
        data-test="reach-tooltip-loading"
        sx={{
          width: 240,
        }}
      >
        <Skeleton animation="wave" width={50} />
        <Skeleton animation="wave" height={60} />
        <Skeleton animation="wave" />
        <Skeleton animation="wave" />
        <Skeleton animation="wave" />
        <Skeleton animation="wave" />
        <Skeleton animation="wave" />
      </Box>
    );
  }

  return (
    <Box
      data-test="reach-tooltip-content"
      sx={{
        width: 240,
      }}
    >
      <Typography variant="body1Bold" gutterBottom data-test="reach-title">
        {t('Breakdown')}
      </Typography>
      <Typography
        variant="body2"
        gutterBottom
        data-test="reach-description"
        sx={{
          lineHeight: '16px',
          mb: 2,
        }}
      >
        {text}
      </Typography>
      <Typography
        variant="body2Bold"
        data-test="reach-values"
        sx={{
          lineHeight: '16px',
        }}
      >
        {renderSections(sections, sectionLabels)}
      </Typography>
    </Box>
  );
}
